/**
 * Shared Origin Cell Component
 *
 * Provides a reusable origin display cell with Chariot rollup pattern.
 * Used across Assets, Seeds, and other entity tables for consistent origin display.
 *
 * Features:
 * - ChipsList display with brand styling
 * - Chariot rollup pattern (native capabilities → "Chariot")
 * - Integration name formatting (github → "GitHub", aws → "AWS")
 * - Manual import detection (email addresses → "Import")
 * - Tooltip showing all origins
 * - Optional filter-based prioritization
 *
 * @module components/cells/OriginCell
 */

import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import { getDisplayOriginTexts } from '@/utils/originDisplay.util';

/**
 * Props for OriginCell component
 */
export interface OriginCellProps {
  /** Raw origin value(s) - can be single string or array */
  origins: string | string[] | undefined | null;

  /** Optional prioritized order of origins (for filter-based sorting) */
  prioritizedOrigins?: string[];

  /** Optional className for the ChipsList container */
  className?: string;

  /** Optional data-testid for testing */
  'data-testid'?: string;
}

/**
 * Shared origin cell renderer with Chariot rollup pattern.
 *
 * Handles both single and multiple origins with automatic deduplication
 * and Chariot capability rollup.
 *
 * @param props - OriginCellProps
 * @returns JSX element with origin chips or dash if no origins
 *
 * @example
 * // Single origin
 * <OriginCell origins="subdomain" />  // → "Chariot" chip
 *
 * @example
 * // Multiple origins
 * <OriginCell origins={["subdomain", "portscanner", "github"]} />
 * // → "Chariot" chip with "+1" (github shown in tooltip)
 *
 * @example
 * // With prioritization (Assets table)
 * <OriginCell
 *   origins={asset.origins}
 *   prioritizedOrigins={sortedOrigins}
 * />
 */
export const OriginCell: React.FC<OriginCellProps> = ({
  origins,
  prioritizedOrigins,
  className = 'max-w-[200px] min-w-[200px]',
  'data-testid': dataTestId = 'origin-chips',
}) => {
  // Fetch filter options for Chariot rollup pattern
  const { data: filterOptions } = useGetVulnerabilityFilters();

  // Normalize origin to array format for consistent processing
  const normalizedOrigins = useMemo(() => {
    if (!origins) return [];
    if (Array.isArray(origins)) return origins;
    return [origins];
  }, [origins]);

  // React Query provides stable references for filterOptions.sourceFilters
  // via structural sharing and caching. Direct access is safe.
  const displayOrigins = useMemo(() => {
    // Use prioritized origins if provided, otherwise use display origins
    if (prioritizedOrigins && prioritizedOrigins.length > 0) {
      return prioritizedOrigins;
    }
    return getDisplayOriginTexts(
      normalizedOrigins,
      filterOptions?.sourceFilters || []
    );
  }, [normalizedOrigins, filterOptions?.sourceFilters, prioritizedOrigins]);

  if (normalizedOrigins.length === 0) {
    return <>-</>;
  }

  return (
    <ChipsList
      items={displayOrigins}
      maxVisible={1}
      variant="brand"
      className={className}
      size="xs"
      data-testid={dataTestId}
      wrapInTooltip
    />
  );
};

OriginCell.displayName = 'OriginCell';
